import React from 'react';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { SCOPES } from '@/features/config/scopes';
import { BrokerGroupChip } from '../../shared/chips/BrokerGroupChip';
import { LedgerChip } from '../../shared/chips/LedgerChip';
import { useAdminConfirm } from '../../shared/AdminConfirmProvider';
import { useCopyFormFieldConfigsMutation } from '../api/formFieldConfigsApi';
import { useFormFieldConfigScope } from '../useFormFieldConfigScope';

/**
 * Where a row of the page's ledger or broker group was copied from (the default, a ledger or a
 * broker group), with a button that copies that config over the row again after a confirm,
 * once there are no unsaved changes. Empty for rows from other scopes, and for rows that
 * aren't copies.
 */
export const FormFieldConfigCopiedFromCell = ({ config }) => {
	const dispatch = useDispatch();
	const confirm = useAdminConfirm();
	const { scope } = useFormFieldConfigScope();
	const { dirty } = useFormikContext();
	const [copyFormFieldConfigs, { isLoading }] = useCopyFormFieldConfigsMutation();

	const { copiedFrom } = config;
	const isScopeCopy =
		copiedFrom && config.scope === scope.scope && config.scopeId === scope.scopeId;
	if (!isScopeCopy) return null;

	const handleResync = async () => {
		const confirmed = await confirm({
			title: 'Resync row?',
			content: `Replace ${config.label || config.name}'s settings with the current settings of the config it was copied from?`,
			confirmText: 'Resync',
		});
		if (!confirmed) return;

		const result = await copyFormFieldConfigs({ docIds: [copiedFrom.id], targets: [scope] });
		if (result.error) {
			dispatch(
				showToast({
					message:
						result.error.status === 404
							? 'The config this was copied from no longer exists.'
							: "Couldn't resync the row. Please try again.",
					error: true,
				}),
			);
			return;
		}
		dispatch(
			showToast({
				message: 'Resynced the row',
				success: true,
				duration: 4000,
				autohide: true,
			}),
		);
	};

	return (
		<Stack
			direction='row'
			spacing={0.5}
			sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}
		>
			{copiedFrom.scope === SCOPES.BROKER_GROUP && (
				<BrokerGroupChip id={copiedFrom.scopeId} />
			)}
			{copiedFrom.scope === SCOPES.LEDGER && <LedgerChip id={copiedFrom.scopeId} />}
			{copiedFrom.scope === SCOPES.DEFAULT && (
				<Typography variant='body2' color='text.disabled'>
					Default
				</Typography>
			)}
			<Tooltip title={dirty ? 'Save or cancel your changes first' : 'Resync row with parent'}>
				<span>
					<IconButton
						size='small'
						aria-label='Resync row with parent'
						disabled={dirty || isLoading}
						onClick={handleResync}
					>
						<RefreshIcon fontSize='small' />
					</IconButton>
				</span>
			</Tooltip>
		</Stack>
	);
};

FormFieldConfigCopiedFromCell.displayName = 'FormFieldConfigCopiedFromCell';
